import { MEAL_LABELS, formatNumber } from "components/day/summary";

// What the chat says back. The models decide and estimate; these are the
// words, so every reply the person reads is one of these — except advice,
// which is Claude's own answer, and the line of feedback Claude writes under a
// log, a change or a cupboard edit (a message's `note`), shown as it is.

export const EXAMPLE = "two eggs and toast for breakfast";

// Short on purpose: the help dialog (components/chat/help) has the rest.
export const GREETING = "Tell me what you ate, what you have in, or ask what to eat.";

// The day screen's headings, lowercased: "add it to snacks".
const mealName = (meal) => (MEAL_LABELS[meal] || meal || "").toLowerCase();

// The reply to one kept bot message, by its kind (see the api's
// common/schema/chat/data). `ask` puts the yes/no buttons under it, for an
// "unsure" still waiting. Food and changes are already done by the time this
// is read, so their reply is what was done.
export const replyFor = ({ kind, entries, items, note, asking = true }) => {
  const lines = entries || [];
  // Food can come with the cupboard: "I had eggs, and I bought tortillas",
  // or "two of the eggs" taking them off it (eaten).
  if (kind === "food") return { text: (items || []).length ? `${logged(lines)} ${eaten(items)}` : logged(lines), ask: false };
  if (kind === "change") return { text: changed(lines), ask: false };
  if (kind === "cupboard") return { text: stocked(items), ask: false };
  if (kind === "advice") return { text: note || noAdvice, ask: false };
  if (kind === "unsure") return { text: "Is this something you ate or drank?", ask: Boolean(asking) };
  if (kind === "error") return { text: unavailable, ask: false };
  return { text: notFood, ask: false };
};

export const notFood = `I log food, keep your cupboard and suggest meals. Try "${EXAMPLE}".`;

export const noAdvice = "I couldn't come up with a suggestion for that. Try asking another way.";

export const unavailable = "I couldn't reach the model. Try again in a moment.";

export const nothingFound = "I couldn't find food in that. Say what you ate.";

const kcal = (entries) => entries.reduce((sum, e) => sum + Number(e.calories || 0), 0);

// "Logged 223 kcal to breakfast." The entries themselves are listed under the
// reply, each a link to fix it.
export const logged = (entries = []) => {
  if (!entries.length) return nothingFound;
  const meals = [...new Set(entries.map((e) => e.meal))];
  const where = meals.length === 1 ? ` to ${mealName(meals[0])}` : "";
  return `Logged ${formatNumber(kcal(entries))} kcal${where}.`;
};

export const cantChange = `I couldn't tell what to change. Name the food, e.g. "the eggs were lunch".`;

const ACTION_WORDS = { changed: "Changed", removed: "Removed", added: "Added" };

// "Changed oatmeal. Removed toast. Added bagel." The lines under it say what
// each one is now.
export const changed = (lines = []) => {
  if (!lines.length) return cantChange;
  return Object.entries(ACTION_WORDS)
    .map(([action, word]) => {
      const foods = lines.filter((l) => l.action === action).map((l) => l.food);
      return foods.length ? `${word} ${foods.join(", ")}.` : null;
    })
    .filter(Boolean)
    .join(" ");
};

export const cantStock = `I couldn't tell what to change in the cupboard. Try "I have eggs and rice".`;

const STOCK_WORDS = { added: "Added", changed: "Updated", removed: "Removed" };

// "Added bread to the cupboard. Updated eggs. Removed milk."
export const stocked = (items) => {
  const lines = items || [];
  if (!lines.length) return cantStock;
  return Object.entries(STOCK_WORDS)
    .map(([action, word]) => {
      const names = lines.filter((l) => l.action === action).map((l) => l.name);
      if (!names.length) return null;
      return `${word} ${names.join(", ")}${action === "added" ? " to the cupboard" : ""}.`;
    })
    .filter(Boolean)
    .join(" ");
};

// What a food message did to the cupboard: what it bought went in, and what
// it ate came out of it — an amount down, or the last of it used up.
// "Added tortillas to the cupboard. Cupboard: eggs 4. Used up yogurt."
export const eaten = (items) => {
  const lines = items || [];
  const of = (action) => lines.filter((l) => l.action === action);
  return [
    of("added").length ? `Added ${of("added").map((l) => l.name).join(", ")} to the cupboard.` : null,
    of("changed").length ? `Cupboard: ${of("changed").map((l) => `${l.name}${l.quantity ? ` ${l.quantity}` : ""}`).join(", ")}.` : null,
    of("removed").length ? `Used up ${of("removed").map((l) => l.name).join(", ")}.` : null,
  ]
    .filter(Boolean)
    .join(" ");
};

// One cupboard line under a reply.
export const itemLine = ({ name, quantity }) => `${name}${quantity ? ` — ${quantity}` : ""}`;

// One line of the list under a reply.
export const entryLine = ({ food, serving, calories }) =>
  `${food}${serving ? ` (${serving})` : ""} — ${formatNumber(calories || 0)} kcal`;
